import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Mail, Activity } from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../store/auth";
import { Button } from "../components/ui/button";

const BAND_CLS = {
  excellent: { text: "text-emerald-700", ring: "stroke-emerald-500", bg: "bg-emerald-50 border-emerald-200", label: "Excelente" },
  good: { text: "text-emerald-700", ring: "stroke-emerald-500", bg: "bg-emerald-50 border-emerald-200", label: "Bueno" },
  fair: { text: "text-amber-700", ring: "stroke-amber-500", bg: "bg-amber-50 border-amber-200", label: "Mejorable" },
  poor: { text: "text-orange-700", ring: "stroke-orange-500", bg: "bg-orange-50 border-orange-200", label: "Bajo" },
  critical: { text: "text-red-700", ring: "stroke-red-500", bg: "bg-red-50 border-red-200", label: "Crítico" },
};

function HealthScoreCard({ data, onClick }) {
  if (!data) return null;
  const band = BAND_CLS[data.band] || BAND_CLS.fair;
  const radius = 42;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (data.score / 100) * circ;
  return (
    <div className={`mt-6 border rounded-md p-5 ${band.bg}`} data-testid="health-score-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className={`h-5 w-5 ${band.text}`} />
          <span className="font-semibold text-slate-900">Health Score del SGSI</span>
        </div>
        <span className={`text-[12px] font-medium ${band.text}`}>{band.label}</span>
      </div>
      <div className="flex items-center gap-6">
        <svg width="110" height="110" viewBox="0 0 110 110" className="-rotate-90">
          <circle cx="55" cy="55" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="9" />
          <circle cx="55" cy="55" r={radius} fill="none" strokeWidth="9" strokeLinecap="round"
                  strokeDasharray={circ} strokeDashoffset={offset} className={band.ring} />
        </svg>
        <div>
          <div className="text-[32px] font-semibold text-slate-900 leading-none" data-testid="health-score-value">{data.score}<span className="text-[16px] text-slate-400">/100</span></div>
          <div className="text-[12px] text-slate-500 mt-1">Indicador único de tu cumplimiento</div>
        </div>
        <div className="flex-1 grid grid-cols-3 gap-3 text-[12px]">
          <Breakdown title="Cumplimiento" pct={data.breakdown.compliance.value} weight={40} />
          <Breakdown title="Evidencias" pct={data.breakdown.evidence_coverage.value} weight={35} />
          <Breakdown title="Documentos" pct={data.breakdown.documents.value} weight={25} />
        </div>
      </div>
    </div>
  );
}

function Breakdown({ title, pct, weight }) {
  return (
    <div className="border border-slate-200 bg-white rounded p-2">
      <div className="text-slate-500">{title} <span className="text-slate-400">·{weight}%</span></div>
      <div className="text-slate-900 font-semibold mt-1">{pct.toFixed ? pct.toFixed(1) : pct}%</div>
      <div className="h-1 bg-slate-100 rounded mt-1 overflow-hidden">
        <div className="h-1 bg-primary" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [digest, setDigest] = useState(null);
  const [health, setHealth] = useState(null);
  const isoActive = (organization?.active_frameworks || []).includes("iso27001");

  useEffect(() => {
    if (!isoActive) return;
    (async () => {
      try { const { data } = await api.get("/dashboard/compliance?framework=iso27001"); setMetrics(data); } catch { /* ignore */ }
      try {
        const { data } = await api.get("/notifications-log");
        const last = (data || []).find((n) => n.kind === "weekly_digest");
        if (last) setDigest(last);
      } catch { /* ignore */ }
      try { const { data } = await api.get("/dashboard/health-score"); setHealth(data); } catch { /* ignore */ }
    })();
  }, [isoActive]);

  return (
    <div data-testid="dashboard-page" className="max-w-5xl">
      <h1 className="text-[24px] font-semibold tracking-tight text-slate-900">Bienvenido a Nimbus, {user?.name}.</h1>
      <p className="text-[14px] text-slate-500 mt-2">Aquí verás el estado de tu cumplimiento.</p>

      {isoActive && <HealthScoreCard data={health} />}

      {digest && user?.role === "owner" && (
        <div className="mt-6 border border-primary/20 bg-primary/5 rounded-md p-4 flex items-start gap-3" data-testid="weekly-digest-card">
          <Mail className="h-5 w-5 text-primary mt-0.5" />
          <div className="flex-1">
            <div className="text-[14px] font-semibold text-slate-900">Resumen semanal disponible</div>
            <p className="text-[13px] text-slate-700 mt-1">
              Esta semana caducan <b>{digest.metadata?.expiring_this_week ?? 0}</b> evidencias
              {(digest.metadata?.expired ?? 0) > 0 && <> y hay <b>{digest.metadata?.expired}</b> ya caducadas</>}
              {(digest.metadata?.controls_affected ?? 0) > 0 && <> ({digest.metadata.controls_affected} controles afectados)</>}.
            </p>
            <div className="text-[12px] text-slate-500 mt-1">
              Generado el {new Date(digest.sent_at).toLocaleString("es-ES")}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/evidences?status=expiring_soon")} data-testid="digest-cta">
            Revisar evidencias
          </Button>
        </div>
      )}

      {!isoActive && (
        <div className="mt-8 border border-slate-200 rounded-md p-6 bg-white" data-testid="dashboard-cta-iso">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /><span className="font-semibold text-slate-900">Activa ISO 27001 para empezar</span></div>
          <p className="text-[14px] text-slate-500 mt-2">Activa el framework, completa el cuestionario inicial y obtén tu Declaración de Aplicabilidad evaluada por IA.</p>
          <Button className="mt-4" onClick={() => navigate("/frameworks")} data-testid="dashboard-go-frameworks">Ir a frameworks</Button>
        </div>
      )}

      {isoActive && metrics && (
        <div className="mt-8 border border-slate-200 rounded-md p-6 bg-white cursor-pointer hover:border-slate-300" onClick={() => navigate("/soa?framework=iso27001")} data-testid="dashboard-iso-widget">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /><span className="font-semibold text-slate-900">ISO 27001:2022</span></div>
            <span className="text-[12px] text-slate-500">Click para ver el SoA</span>
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-[32px] font-semibold text-slate-900">{metrics.compliance_percentage}%</span>
            <span className="text-[12px] text-slate-500">de cumplimiento</span>
          </div>
          <div className="mt-2 h-2 bg-slate-100 rounded">
            <div className="h-2 bg-primary rounded" style={{ width: `${metrics.compliance_percentage}%` }} />
          </div>
          <div className="mt-4 grid grid-cols-4 gap-3 text-[12px]">
            <Stat label="Implementados" value={`${metrics.implemented_count}/${metrics.applicable_count}`} />
            <Stat label="En progreso" value={metrics.in_progress_count} />
            <Stat label="No iniciados" value={metrics.not_started_count} />
            <Stat label="No aplican" value={metrics.not_applicable_count} />
          </div>
          {metrics.upcoming_reviews?.length > 0 && (
            <div className="mt-5">
              <div className="text-[12px] font-semibold text-slate-700 mb-2">Próximas revisiones</div>
              <ul className="text-[12px] text-slate-600 space-y-1">
                {metrics.upcoming_reviews.map((r) => (<li key={r.control_code}>{r.control_code} · {r.last_review_date ? new Date(r.last_review_date).toLocaleDateString("es-ES") : "Sin revisar"}</li>))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="text-[12px] text-slate-500">{label}</div>
      <div className="text-[16px] font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
